import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { LOGO_ACCEPT, uploadTokenLogo, validateLogoFile } from "@/lib/logos";
import { useWallet } from "@/contexts/WalletContext";

const uploadError = (err) => {
  if (err?.code === "ACTION_REJECTED" || err?.info?.error?.code === 4001) return "Signature was rejected in your wallet";
  return err?.response?.data?.detail || "Logo upload failed";
};

/** Upload/replace a token logo; the deployer's wallet signs to prove ownership. */
export const useLogoUpload = () => {
  const { address, getProvider } = useWallet();
  const queryClient = useQueryClient();
  return async (tokenAddress, file) => {
    const invalid = validateLogoFile(file);
    if (invalid) throw new Error(invalid);
    const res = await uploadTokenLogo({ eip1193: getProvider(), owner: address, address: tokenAddress, file });
    queryClient.invalidateQueries({ queryKey: ["portfolio"] });
    return res;
  };
};

export const LogoUploadButton = ({ tokenAddress, symbol, onUploaded }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = useLogoUpload();

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const res = await upload(tokenAddress, file);
      toast.success(`${symbol} logo updated`);
      onUploaded?.(res);
    } catch (err) {
      toast.error(err instanceof Error && !err.response && !err.code ? err.message : uploadError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input ref={input} type="file" accept={LOGO_ACCEPT} className="hidden" onChange={onPick} data-testid={`logo-file-${symbol.toLowerCase()}`} />
      <button data-testid={`logo-upload-${symbol.toLowerCase()}`} onClick={() => input.current?.click()} disabled={busy}
        className="inline-flex items-center gap-1.5 text-xs text-[#D4AF37] hover:text-[#F3E5AB] disabled:opacity-50 transition-colors">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />} {busy ? "Uploading…" : "Set logo"}
      </button>
    </>
  );
};
